import { Link } from 'react-router-dom';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { useServerFlag } from '../../hooks/useServerFlag';
import { Micro } from '../primitives';
import styles from './QuickActions.module.css';

/**
 * Desktop right-rail quick-actions cluster on `/v2/today`.
 * Primary "Ask coach" CTA in ink-900 with marine icon circle;
 * 3 ghost secondary actions (Log race, Log session, Update
 * context) below. Replaces mobile's FAB on desktop.
 *
 * **PR 8 update (2026-05-10) — wired**:
 *   - "Ask coach" → `/v2/chat`
 *   - "Log a race" → `/v2/races?openRaceForm=1` (route honors
 *     the deep-link param to auto-open the form modal/sheet)
 *   - "Update context" → `/v2/settings#airo-knows` (F-513: anchors to the
 *     read-only "What Airo knows" section so the link lands on the context
 *     view, not a bare Settings top)
 *
 * **W1 update (2026-05-30) — removed dead "Log a session"**:
 *   The disabled "Log a session" button had no route or flow and just
 *   advertised a dead end. Ad-hoc session logging (recording a workout
 *   that differs from the planned day) is a real future feature, but
 *   its home is the week-plan day-row on Today, not a quick-action.
 *   Captured in the Today Design brief; button removed here.
 *
 * Per direction.md (locked): "QuickActions on /today is the
 * principal entry point for both routes [Settings + Races] at
 * Default+." This is the polish that makes Phase 6b1's surfaces
 * navigable end-to-end.
 */
export function QuickActions() {
  const iconsOn = useIconSystem();
  // 2.0 wave 2 (spec 7.3 RP-10): with `races_2_0_v1` on the athlete row reads "Add a race"; the target stays.
  const racesV2 = useServerFlag('races_2_0_v1');
  // W2-UX-FIX2 (pixel check 2026-10-18): with the same flag the primary row reads "Ask a coach", the ratified label
  // the side nav already prints. Not keyed on `today_2_0_v1`: the Today states that stay on the legacy page with
  // that flag on (TWR-5) must keep this rail as it was. Flag off, "Ask coach" as before.
  return (
    <section className={styles.actions}>
      <Micro as="div" className={styles.label}>
        Quick actions
      </Micro>
      <Link to="/chat" className={styles.primary}>
        <div className={styles.primaryIcon} aria-hidden>
          {/* A2 — `sys.play` @14 (the "Ask coach" primary). */}
          {iconsOn ? (
            <Icon name="sys.play" size={14} />
          ) : (
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l12 6-12 6z" />
            </svg>
          )}
        </div>
        <div>
          <div className={styles.primaryTitle}>{racesV2 ? 'Ask a coach' : 'Ask coach'}</div>
          <div className={styles.primarySub}>Chat · voice · photo</div>
        </div>
      </Link>
      <div className={styles.group}>
        <Link to="/races?openRaceForm=1" className={styles.action}>
          {/* A2 · R7 — `sys.plus` @14. Optical: 14-grid @2 → ratified 24-grid @1.5. */}
          {iconsOn ? (
            <Icon name="sys.plus" size={14} />
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M7 2v10M2 7h10" />
            </svg>
          )}
          {racesV2 ? 'Add a race' : 'Log a result'}
        </Link>
        <Link to="/settings#airo-knows" className={styles.action}>
          {/* A2 — `sys.person` @14. */}
          {iconsOn ? (
            <Icon name="sys.person" size={14} />
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <circle cx="7" cy="5" r="2.5" />
              <path d="M2 12c0-3 3-5 5-5s5 2 5 5" />
            </svg>
          )}
          Update context
        </Link>
      </div>
    </section>
  );
}
